import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {AuthenticationService} from '../authentication.service';
import {describeNativeAccountError, describeProviderStartRefusal} from '../local/local-account-errors';

/** The three federated doors, in the order they are offered. */
export type LinkableProvider = 'microsoft' | 'apple' | 'google';

/**
 * The four ways to give this login an account (#279) — Microsoft, Apple, Google, or an e-mail address and a
 * password — as ONE component, so the surfaces that offer them cannot drift apart again.
 *
 * ## Why this exists
 *
 * There were three surfaces and three answers. The migration notice offered all four. The management profile
 * offered three. The employee profile offered one — Microsoft — and the notice's own footer told people to go
 * there, which is how a popup came to promise a screen that could not keep the promise. Each surface had grown
 * its own copy of the same handful of handlers, so every door added since has had to be added three times, and
 * twice it was not.
 *
 * The doors are presentation *and* a rule, and it is the rule that made three copies expensive:
 *
 * > **A session that has not been claimed yet CLAIMS; a session that already came in through an account LINKS.**
 *
 * The two are different server flows. `claim/start` hands the legacy login's roles over and redeems any
 * invitation bound to the address; `link/start` only attaches an authenticator. Linking a legacy session with
 * the plain flow leaves a half-migrated account — signed in, holding none of the grants it should have — which
 * is exactly what the employee profile did, silently, for every employee the notice sent there.
 * {@link ProfileComponent} had already worked this out and written the rule down; it now lives here once
 * instead of being re-derived per screen.
 *
 * ## What it deliberately does not own
 *
 * No heading and no card. Each host frames these differently — a dialog step on the notice, a row inside
 * „Anmeldung &amp; Authentifizierung" on the two profiles — and a component that brought its own heading would
 * have to be argued out of it on two of the three.
 *
 * Transitional in one respect only: when the legacy authorization system goes at #264 the claim branch below
 * goes with it and every door becomes a plain link. The component itself stays.
 */
@Component({
  selector: 'auth-account-link-options',
  standalone: true,
  imports: [FormsModule],
  template: `
    <!-- One button per provider rather than a single "link" action: which account somebody uses is their
         decision, and offering only Microsoft would quietly make it for them. -->
    <div [class]="providerRowClass">
      <button type="button" (click)="start('microsoft')" [disabled]="busy" [class]="providerButtonClass">
        <svg class="h-4 w-4" viewBox="0 0 23 23" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/>
          <path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/>
        </svg>
        Mit Microsoft verknüpfen
      </button>

      <button type="button" (click)="start('apple')" [disabled]="busy" [class]="providerButtonClass">
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"
             aria-hidden="true">
          <path d="M17.05 12.54c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.72-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.87-.76-1.48.02-2.84.86-3.6 2.18-1.53 2.66-.39 6.6 1.1 8.76.73 1.06 1.6 2.25 2.74 2.2 1.1-.04 1.52-.71 2.85-.71 1.33 0 1.7.71 2.87.69 1.18-.02 1.93-1.08 2.65-2.14.84-1.23 1.18-2.42 1.2-2.48-.03-.01-2.3-.88-2.32-3.5zM14.9 5.6c.6-.74 1.01-1.76.9-2.78-.87.04-1.93.58-2.56 1.31-.56.65-1.06 1.7-.93 2.7.97.08 1.97-.49 2.59-1.23z"/>
        </svg>
        Mit Apple verknüpfen
      </button>

      <button type="button" (click)="start('google')" [disabled]="busy" [class]="providerButtonClass">
        <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path fill="#4285F4" d="M23.5 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.53 5.53 0 0 1-2.4 3.63v3h3.87c2.26-2.09 3.56-5.17 3.56-8.87z"/>
          <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.87-3a7.2 7.2 0 0 1-10.72-3.78h-4v3.09A12 12 0 0 0 12 24z"/>
          <path fill="#FBBC05" d="M5.35 14.31a7.19 7.19 0 0 1 0-4.6v-3.1h-4a12 12 0 0 0 0 10.8l4-3.1z"/>
          <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0A12 12 0 0 0 1.35 6.62l4 3.09A7.2 7.2 0 0 1 12 4.75z"/>
        </svg>
        Mit Google verknüpfen
      </button>
    </div>

    <!-- A provider start the server refused for a reason the reader should hear (#854: an impersonation). Beside
         the buttons it is about, on every surface that renders them, rather than in the host's generic
         "could not be started" alert, which would read as a fault to retry. -->
    @if (providerError) {
      <p class="mt-2 text-sm text-red-700" role="alert">{{ providerError }}</p>
    }

    <!-- The fourth door. It is a FORM rather than a button because there is no third party to send anybody to:
         an e-mail address and a password are set here and the claim finishes on this screen.

         Collapsed behind a toggle rather than shown expanded, and the three provider buttons keep the top: the
         person reading this already has a username and a password, and a form asking for another pair is the
         one thing on the screen most easily misread as "type your existing login again". Opening it is a
         deliberate act, and the heading above it says what the address is for. -->
    @if (!nativeOpen) {
      <button type="button" (click)="nativeOpen = true" [disabled]="busy" [class]="nativeToggleClass">
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
             aria-hidden="true">
          <rect x="2" y="4" width="20" height="16" rx="2"/>
          <path d="m2 7 10 6 10-6"/>
        </svg>
        Mit E-Mail-Adresse und Passwort
      </button>
    } @else {
      <form class="mt-2 space-y-3 rounded-md border-2 border-gray-200 p-3" (ngSubmit)="claimWithPassword()">
        <p class="text-sm font-medium text-gray-900">Neues Konto aus E-Mail-Adresse und Passwort</p>
        <p class="text-xs text-gray-500">
          Wählen Sie eine E-Mail-Adresse, unter der Sie sich künftig anmelden. Haben Sie hier bereits ein
          Konto mit dieser Adresse, geben Sie dessen Passwort ein — dann wird dieser Zugang damit verknüpft.
        </p>

        <div>
          <label [attr.for]="idPrefix + '-email'" class="block text-sm font-medium text-gray-700">E-Mail-Adresse</label>
          <input [attr.id]="idPrefix + '-email'" name="email" type="email" autocomplete="email" required
                 [ngModel]="email" (ngModelChange)="email = $event" [disabled]="busy"
                 class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"/>
        </div>

        <div>
          <label [attr.for]="idPrefix + '-password'" class="block text-sm font-medium text-gray-700">Passwort</label>
          <input [attr.id]="idPrefix + '-password'" name="password" type="password" autocomplete="new-password"
                 required [ngModel]="password" (ngModelChange)="password = $event" [disabled]="busy"
                 [attr.aria-describedby]="idPrefix + '-password-rules'"
                 class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"/>
          <p [attr.id]="idPrefix + '-password-rules'" class="mt-1 text-xs text-gray-500">
            8–32 Zeichen, mit Groß- und Kleinbuchstabe, Ziffer und einem Sonderzeichen (! &#64; # $ &amp; *).
          </p>
        </div>

        <!-- Password confirmation (#577). Never sent to the server — see claimWithPassword — its only job is
             catching a typo before this claim mints a password nobody else knows either. -->
        <div>
          <label [attr.for]="idPrefix + '-confirm-password'" class="block text-sm font-medium text-gray-700">
            Passwort bestätigen
          </label>
          <input [attr.id]="idPrefix + '-confirm-password'" name="confirm-password" type="password"
                 autocomplete="new-password" required
                 [ngModel]="confirmPassword" (ngModelChange)="confirmPassword = $event" [disabled]="busy"
                 class="mt-1 block w-full rounded-md border-2 border-gray-200 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-primary-700 sm:text-sm sm:leading-6"/>
        </div>

        <!-- Shown only once there is something to compare — an empty confirmation is unfinished, not a
             mismatch yet. -->
        @if (confirmPassword && confirmPassword !== password) {
          <p class="text-sm text-red-700" role="alert">Die Passwörter stimmen nicht überein.</p>
        }

        @if (error) {
          <p class="text-sm text-red-700" role="alert">{{ error }}</p>
        }

        <button type="submit" [disabled]="busy || !confirmPassword || confirmPassword !== password"
                class="w-full rounded-md bg-theme-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-theme-primary-900 disabled:opacity-50">
          Konto anlegen und verknüpfen
        </button>
      </form>
    }
  `
})
export class AccountLinkOptionsComponent {
  /**
   * Whether this session is an employee rather than a management user.
   *
   * Only the three PROVIDER doors need it: their callback arrives as a top-level navigation with no bearer, so
   * the flow has to be told at the start. The native door below passes nothing — the server classifies the
   * token it is already holding, which is both simpler and not forgeable.
   */
  @Input() asEmployee = false;

  /**
   * Where the provider should come back to. Defaults to the current page, which is what every host wants: the
   * claim is an interruption, not a detour.
   *
   * Overridable because a host may sit at a route the provider must not be handed verbatim — the management
   * profile pins its own so a query string picked up along the way is not carried into the OIDC round trip.
   */
  @Input() returnUrl: string | null = null;

  /**
   * `stacked` for a narrow dialog (the migration notice, `sm:max-w-lg` — three buttons side by side there are
   * cramped to the point of truncation), `inline` for a full-width profile row, where they wrap.
   */
  @Input() layout: 'stacked' | 'inline' = 'stacked';

  /**
   * Prefix for the `id`s in the native form, so the `for`/`id` pairs stay unique.
   *
   * They were fixed strings while only one instance could exist on a page. Two can now — the notice renders
   * over a profile — and duplicate ids do not fail loudly: the browser resolves every `for` to the FIRST match,
   * so the label of the second form would focus and name the field of the first.
   */
  @Input() idPrefix = 'account-link';

  /** The native claim SUCCEEDED. Nothing navigated; the host decides what to say. */
  @Output() claimed = new EventEmitter<void>();

  /**
   * A PROVIDER door could not be started — infrastructure, with nothing for the reader to correct. Emitted
   * rather than reported here because the three hosts alert differently, and a component that picked one would
   * be making a host's decision. The native refusals are NOT emitted: they are rendered inline beside the field
   * they are about (see {@link claimWithPassword}).
   */
  @Output() failed = new EventEmitter<unknown>();

  /** Mirrors {@link busy}, so a host can disable its own controls for the same window. */
  @Output() busyChange = new EventEmitter<boolean>();

  protected nativeOpen = false;
  protected email = '';
  protected password = '';

  /**
   * The password confirmation (#577). Compared client-side only — see {@link claimWithPassword} — and never
   * sent to the server.
   */
  protected confirmPassword = '';

  /** The last native refusal, already translated. Null while there is nothing to say. */
  protected error: string | null = null;

  /** The last provider-start refusal with a reason worth saying (#854), already translated. */
  protected providerError: string | null = null;

  private _busy = false;

  constructor(private auth: AuthenticationService) {
  }

  protected get busy(): boolean {
    return this._busy;
  }

  protected get providerRowClass(): string {
    return this.layout === 'inline'
      ? 'flex flex-col gap-2 sm:flex-row sm:flex-wrap'
      : 'space-y-2';
  }

  protected get providerButtonClass(): string {
    const shape = this.layout === 'inline'
      ? 'inline-flex items-center justify-center gap-2 px-3 py-1.5'
      : 'flex w-full items-center justify-center gap-2 px-3 py-2';

    return `${shape} rounded-md border-2 border-gray-200 bg-white text-sm font-semibold text-gray-700 `
      + 'shadow-sm hover:bg-gray-50 disabled:opacity-50';
  }

  /**
   * The fourth door's toggle, shaped by the SAME rule as the three provider buttons.
   *
   * It used to carry a hardcoded class string ending in `sm:w-auto sm:px-3 sm:py-1.5` — the inline shape, applied
   * unconditionally. In the `inline` layout that was right; in the `stacked` one (the migration notice, which is
   * where most people meet this) it rendered the e-mail + password option visibly narrower and shorter than the
   * three buttons above it, from the `sm` breakpoint up. On a phone the difference was invisible, which is why it
   * survived: it only shows on a desktop.
   *
   * That is not cosmetic here. This is the one door that needs no account anywhere else, so it is the only option
   * some people can actually use — and it was the one rendered as a lesser, secondary action. Deriving its shape
   * from `layout`, exactly as {@link providerButtonClass} does, is what keeps the four offers looking like four
   * equal offers, which is the whole reason the buttons were written one-per-provider in the first place.
   */
  protected get nativeToggleClass(): string {
    // The toggle sits OUTSIDE the provider row (it has to: its @else branch is the form), so it does not get
    // the row's stretch. In the inline layout the row is `flex-col` on a phone and the three provider buttons
    // fill the width as its children; the toggle, reusing the chip shape, stayed chip-wide underneath them.
    // So on a phone it takes the full width itself and only becomes a chip from `sm` up, where the row is a
    // wrapping line of chips too. The stacked layout is already full-width at every size.
    if (this.layout === 'inline') {
      return 'mt-2 flex w-full items-center justify-center gap-2 px-3 py-2 sm:inline-flex sm:w-auto sm:py-1.5 '
        + 'rounded-md border-2 border-gray-200 bg-white text-sm font-semibold text-gray-700 shadow-sm '
        + 'hover:bg-gray-50 disabled:opacity-50';
    }
    return `mt-2 ${this.providerButtonClass}`;
  }

  /**
   * Starts a federated door: the claim for a session that has not been migrated yet, the plain link for one
   * that already came in through an account.
   *
   * The distinction is {@link AuthenticationService.isExternalSession} and it is the whole reason this rule is
   * in one place — see the class remarks. Note it answers `false` for a NATIVE (e-mail + password) identity
   * too, which is correct here for the same reason it is correct there: such a session's password lives with
   * us, and a legacy login behind it is still a legacy login.
   */
  protected start(provider: LinkableProvider): void {
    if (this.busy) return;
    this.setBusy(true);
    this.providerError = null;

    const returnUrl = this.returnUrl ?? `${location.origin}${location.pathname}`;

    if (this.auth.isExternalSession) {
      // These two navigate from inside the service and answer nothing, so there is no completion to wait for.
      if (this.asEmployee) this.auth.linkMicrosoftAsEmployee(returnUrl, provider);
      else this.auth.linkMicrosoft(returnUrl, provider);

      this.setBusy(false);
      return;
    }

    this.auth.claimForMicrosoftAccount(returnUrl, this.asEmployee ? 'employee' : undefined, provider).subscribe({
      next: r => window.open(r.url, '_self'),
      error: err => {
        this.setBusy(false);

        // #854: an impersonation session may not claim the login it is impersonating. That is not a fault to
        // retry, so it is said here, in German, instead of being handed to the host's generic alert.
        const refusal = describeProviderStartRefusal(err?.error?.code);
        if (refusal) {
          this.providerError = refusal;
          return;
        }

        this.failed.emit(err);
      }
    });
  }

  /**
   * Claims this login for an e-mail + password account, without leaving the page.
   *
   * The refusal is rendered INLINE, beside the field it is about, rather than handed to the host. The two are
   * different kinds of failure: "the redirect would not start" is infrastructure and there is nothing to
   * correct, whereas a weak password or an address that is already somebody's account is something the person
   * fixes in the form they are looking at — and a toast that disappears is the wrong place to say so.
   *
   * `describeNativeAccountError` is the same mapping the invitation landing uses, deliberately: these are the
   * identical server-side refusals, and a second table of German sentences for them would drift.
   *
   * Deliberately does NOT sign anybody in or reload. The claim issues no session — the caller is already signed
   * in on the login being migrated, and it keeps working exactly as it did.
   */
  protected claimWithPassword(): void {
    // Belt-and-braces: the submit button is already disabled for both conditions.
    if (this.busy || !this.confirmPassword || this.confirmPassword !== this.password) return;
    this.setBusy(true);
    this.error = null;

    this.auth.claimWithPassword(this.email, this.password).subscribe({
      next: () => {
        this.setBusy(false);
        this.claimed.emit();
      },
      error: err => {
        this.setBusy(false);
        this.error = describeNativeAccountError(err?.error?.code);
      }
    });
  }

  private setBusy(value: boolean): void {
    this._busy = value;
    this.busyChange.emit(value);
  }
}
